import { getCookieConsentValue, resetCookieConsentValue } from "react-cookie-consent";
import httpClient from "@/config/httpClient";
import { endpoints } from "@/utils/common/requests/endpoints";
import {
	CONSENT_COOKIE_NAME,
	CONSENT_POLICY_VERSION,
	CONSENT_PROOF_STORAGE_KEY,
	VISITOR_ID_STORAGE_KEY,
} from "./constants";

export { CONSENT_COOKIE_NAME, CONSENT_POLICY_VERSION };

/**
 * Consentimiento de cookies no exentas (Bugsnag).
 * Un consentimiento aceptado bajo una versión anterior de la política no es
 * válido: se retira la cookie para que el banner vuelva a mostrarse.
 * @returns {boolean}
 */
export function hasAnalyticsConsent() {
	const consent = getCookieConsentValue(CONSENT_COOKIE_NAME) === "true";
	if (!consent) return false;

	try {
		const proof = JSON.parse(localStorage.getItem(CONSENT_PROOF_STORAGE_KEY) || "null");
		if (proof?.policyVersion === CONSENT_POLICY_VERSION) return true;
	} catch {
		// Prueba ilegible: tratamos el consentimiento como caducado.
	}

	resetCookieConsentValue(CONSENT_COOKIE_NAME);
	return false;
}

/**
 * Identificador anónimo y persistente del navegador, usado para la prueba
 * de consentimiento cuando no hay sesión iniciada.
 * @returns {string}
 */
export function getVisitorId() {
	let visitorId = localStorage.getItem(VISITOR_ID_STORAGE_KEY);

	if (!visitorId) {
		visitorId = crypto.randomUUID();
		localStorage.setItem(VISITOR_ID_STORAGE_KEY, visitorId);
	}

	return visitorId;
}

/**
 * Registra la prueba de consentimiento (qué, cuándo, versión de política):
 * - localStorage (prueba local)
 * - backend (POST público, sin IP/UA)
 *
 * Fire-and-forget: nunca lanza.
 * @param {object} params
 * @param {boolean} params.accepted
 * @param {string} params.source Origen del consentimiento ("banner" | "settings")
 * @param {string} [params.userId]
 */
export async function recordConsent({ accepted, source, userId }) {
	const proof = {
		accepted,
		policyVersion: CONSENT_POLICY_VERSION,
		source,
		at: new Date().toISOString(),
	};

	try {
		localStorage.setItem(CONSENT_PROOF_STORAGE_KEY, JSON.stringify(proof));
	} catch {
		// Sin almacenamiento local (p. ej. modo privado): la prueba de backend sigue.
	}

	try {
		await httpClient.post(endpoints.consent.POST(), {
			visitorId: getVisitorId(),
			userId: userId ?? null,
			accepted,
			policyVersion: CONSENT_POLICY_VERSION,
			source,
		});
	} catch {
		// La prueba local ya queda registrada; no bloqueamos la UX por un fallo de red.
	}
}
